import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import { useTicketingUser } from '../../context/TicketingUserContext';
import { getWatchers, addWatcher, removeWatcher } from '../../api/ticketApi';
import WatcherList from './WatcherList';
import AddWatcherInput from './AddWatcherInput';

export default function WatcherSection({ ticketId }) {
  const { isAdmin } = useTicketingUser();
  const [watchers, setWatchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadWatchers = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getWatchers(ticketId);
      setWatchers(Array.isArray(list) ? list : list?.watchers ?? []);
    } catch (err) {
      setError(err?.json?.message || 'Failed to load watchers.');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (isAdmin && ticketId) loadWatchers();
  }, [isAdmin, ticketId, loadWatchers]);

  const handleAdd = async (user) => {
    setError(null);
    try {
      await addWatcher(ticketId, user.userId);
      setWatchers((prev) => [...prev, user]);
    } catch (err) {
      setError(err?.json?.message || 'Failed to add watcher.');
    }
  };

  const handleRemove = async (userId) => {
    const prev = [...watchers];
    setWatchers((w) => w.filter((x) => x.userId !== userId));
    setError(null);
    try {
      await removeWatcher(ticketId, userId);
    } catch (err) {
      setError(err?.json?.message || 'Failed to remove watcher.');
      setWatchers(prev);
    }
  };

  if (!isAdmin) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          Watchers ({watchers.length})
        </Typography>
        <WatcherList
          watchers={watchers}
          onRemove={handleRemove}
          error={error}
          onDismissError={() => setError(null)}
        />
        <AddWatcherInput ticketId={ticketId} onAdded={handleAdd} />
      </CardContent>
    </Card>
  );
}
